/**
 * Workspaces context: the active org's workspaces (Phase A: rows live in the
 * legacy `brands` table, mapped onto the `Workspace` shape), the active
 * workspace selection, and workspace CRUD + targeted refetch.
 *
 * Split out of the legacy monolithic `OrgProvider` (audit item 26). Nests
 * inside `OrgCoreProvider` and reads `activeOrg` from it.
 *
 * @module lib/WorkspacesProvider
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSupabase } from './SupabaseProvider';
import { useOrgCore } from './OrgCoreProvider';
import { captureException } from './telemetry/posthog/frontend';
import type { Workspace } from './org-types';
import { ACTIVE_WORKSPACE_STORAGE_KEY } from './org-types';

/**
 * Columns selected when loading workspaces from the `brands` table. Kept as a
 * module-level constant so tests can lock the contract by substring-asserting
 * the projection.
 *
 * `research_provider` and `research_error` are deliberately absent: every
 * member loads this, nothing displays them, and rows written before the
 * public error codes hold raw provider text and the vendor name. Research
 * failure state comes from `research_status`.
 */
const WORKSPACE_SELECT_COLUMNS =
  'id, organization_id, name, slug, website_url, privacy_policy_url, logo_url, icon_url, color, is_default, summary, target_audience, brand_voice, brand_tone, value_proposition, keywords, suggested_objectives, suggested_objectives_generated_at, brand_name_canonical, offerings, voice_attributes, tone_attributes, style_guide, colors, fonts, research_status, research_started_at, research_completed_at, created_at, updated_at';

/**
 * Resolve which workspace should be active for a freshly loaded list.
 *
 * Priority: the stored id (if still present) -> the `is_default` row -> the
 * first row -> `null` when the list is empty. Pure function, used both at
 * initial load and after a workspace fetch.
 */
function resolveActiveWorkspace(
  list: Workspace[],
  storedId: string | null,
): Workspace | null {
  if (list.length === 0) return null;
  if (storedId) {
    const stored = list.find((w) => w.id === storedId);
    if (stored) return stored;
  }
  return list.find((w) => w.is_default) ?? list[0];
}

/** Shape of the workspaces context value. */
export interface WorkspacesContextType {
  /** Workspaces belonging to the active org. */
  workspaces: Workspace[];
  /** Currently active workspace, or null. */
  activeWorkspace: Workspace | null;
  /** Switch the active workspace by id (persisted to localStorage). */
  setActiveWorkspaceId: (id: string) => void;
  /** User-facing error from the most recent workspaces fetch, or null. */
  workspacesError: string | null;
  /** Whether the most recent workspaces fetch is in flight. */
  workspacesLoading: boolean;
  /** Re-run the workspaces fetch for the active org. */
  refreshWorkspaces: () => Promise<void>;
  /** Re-fetch only the active workspace's row by id. */
  refreshActiveWorkspace: () => Promise<Workspace | null>;
  /** Create a new workspace inside the active org. */
  createWorkspace: (name: string, websiteUrl: string | null) => Promise<Workspace>;
}

const WorkspacesContext = createContext<WorkspacesContextType | undefined>(
  undefined,
);

/** Props for the WorkspacesProvider component. */
interface WorkspacesProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the workspaces context. Must nest inside `OrgCoreProvider` (it
 * reads the active org from there).
 */
export const WorkspacesProvider: React.FC<WorkspacesProviderProps> = ({
  children,
}) => {
  const { user, supabase } = useSupabase();
  const { activeOrg } = useOrgCore();

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState<Workspace | null>(
    null,
  );
  const [workspacesError, setWorkspacesError] = useState<string | null>(null);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  /**
   * Tracks which org id last loaded workspaces so the org-change effect can
   * detect a real org switch and clear the persisted workspace id (a workspace
   * from org A must not leak into org B's session).
   */
  const lastWorkspacesOrgIdRef = useRef<string | null>(null);

  /** Stable ref for the supabase client to avoid re-triggering effects. */
  const supabaseRef = useRef(supabase);
  supabaseRef.current = supabase;

  /**
   * Latest-wins guard (A11): the org id of the most recent workspaces fetch.
   * A slow response for org A that lands after the user switched to org B is
   * discarded (its stamp no longer matches), so B never shows A's workspaces.
   */
  const latestWorkspacesOrgRef = useRef<string | null>(null);

  /**
   * Load the active org's workspaces from the `brands` table. On success
   * replaces the list, resolves `activeWorkspace`, and clears the error. On
   * failure captures to PostHog, surfaces a message, and clears the list so
   * stale data can't leak. Failure does not block the rest of org loading.
   * A response that arrives after the active org changed is dropped via the
   * latest-wins stamp.
   */
  const fetchWorkspaces = useCallback(
    async (organizationId: string) => {
      latestWorkspacesOrgRef.current = organizationId;
      setWorkspacesLoading(true);
      try {
        const { data, error } = await supabaseRef.current
          .from('brands')
          .select(WORKSPACE_SELECT_COLUMNS)
          .eq('organization_id', organizationId);

        // Drop a stale response: the active org changed mid-flight.
        if (latestWorkspacesOrgRef.current !== organizationId) return;

        if (error) {
          captureException(
            new Error(error.message),
            'WorkspacesProvider.loadWorkspaces',
            { organizationId, userId: user?.id },
          );
          setWorkspaces([]);
          setActiveWorkspaceState(null);
          setWorkspacesError(error.message);
          return;
        }

        const list = (data ?? []) as unknown as Workspace[];
        setWorkspaces(list);

        let storedId: string | null = null;
        try {
          storedId = localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY);
        } catch {
          // localStorage unavailable; non-fatal
        }
        setActiveWorkspaceState(resolveActiveWorkspace(list, storedId));
        setWorkspacesError(null);
      } catch (err) {
        if (latestWorkspacesOrgRef.current !== organizationId) return;
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'WorkspacesProvider.loadWorkspaces',
          { organizationId, userId: user?.id },
        );
        setWorkspaces([]);
        setActiveWorkspaceState(null);
        setWorkspacesError(err instanceof Error ? err.message : String(err));
      } finally {
        // Only the latest request owns the loading flag; a stale response must
        // not flip it off while the current fetch is still running.
        if (latestWorkspacesOrgRef.current === organizationId) {
          setWorkspacesLoading(false);
        }
      }
    },
    [user?.id],
  );

  /** Re-fetch the workspaces list for the active org. */
  const refreshWorkspaces = useCallback(async () => {
    const orgId = activeOrg?.id;
    if (!orgId) return;
    await fetchWorkspaces(orgId);
  }, [activeOrg?.id, fetchWorkspaces]);

  /**
   * Track the active workspace id in a ref so the targeted refetch helper
   * does not re-bind whenever `activeWorkspace` changes.
   */
  const activeWorkspaceIdRef = useRef<string | null>(null);
  activeWorkspaceIdRef.current = activeWorkspace?.id ?? null;

  /**
   * Re-fetch the active workspace's row by id (NOT the full org list) and
   * splice it into local state. Used by the form-builder polling loop.
   */
  const refreshActiveWorkspace =
    useCallback(async (): Promise<Workspace | null> => {
      const id = activeWorkspaceIdRef.current;
      if (!id) return null;
      try {
        const { data, error } = await supabaseRef.current
          .from('brands')
          .select(WORKSPACE_SELECT_COLUMNS)
          .eq('id', id)
          .maybeSingle();

        if (error) {
          captureException(
            new Error(error.message),
            'WorkspacesProvider.refreshActiveWorkspace',
            { workspaceId: id, userId: user?.id },
          );
          return null;
        }

        if (!data) return null;
        const next = data as unknown as Workspace;
        setWorkspaces((prev) => {
          const idx = prev.findIndex((w) => w.id === next.id);
          if (idx === -1) return [...prev, next];
          const copy = prev.slice();
          copy[idx] = next;
          return copy;
        });
        setActiveWorkspaceState((prev) =>
          prev && prev.id === next.id ? next : prev,
        );
        return next;
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'WorkspacesProvider.refreshActiveWorkspace',
          { workspaceId: id, userId: user?.id },
        );
        return null;
      }
    }, [user?.id]);

  /**
   * Set the active workspace by id. Persists to localStorage. No-op when the
   * id is not in the current `workspaces` list (guards cross-org or deleted).
   */
  const setActiveWorkspaceId = useCallback(
    (id: string) => {
      const next = workspaces.find((w) => w.id === id);
      if (!next) return;
      setActiveWorkspaceState(next);
      try {
        localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, id);
      } catch {
        // localStorage unavailable; non-fatal
      }
    },
    [workspaces],
  );

  /**
   * Create a new workspace inside the active org (Phase A: the legacy `brands`
   * table). On success the row is added to local state and made active.
   */
  const createWorkspace = useCallback(
    async (name: string, websiteUrl: string | null): Promise<Workspace> => {
      if (!activeOrg?.id) {
        const err = new Error('No active organization');
        captureException(err, 'WorkspacesProvider.createWorkspace', {
          userId: user?.id,
        });
        throw err;
      }
      if (!user?.id) {
        const err = new Error('Not authenticated');
        captureException(err, 'WorkspacesProvider.createWorkspace', {
          organizationId: activeOrg.id,
        });
        throw err;
      }

      const trimmedUrl = (websiteUrl ?? '').trim();
      const insertPayload = {
        name,
        organization_id: activeOrg.id,
        owner_id: user.id,
        website_url: trimmedUrl.length > 0 ? trimmedUrl : null,
        is_default: false,
      };

      try {
        const { data, error } = await supabaseRef.current
          .from('brands')
          .insert(insertPayload)
          .select(WORKSPACE_SELECT_COLUMNS)
          .single();

        if (error) {
          captureException(
            new Error(error.message),
            'WorkspacesProvider.createWorkspace',
            { organizationId: activeOrg.id, userId: user.id },
          );
          throw new Error(error.message);
        }

        const created = data as unknown as Workspace;
        setWorkspaces((prev) => [...prev, created]);
        setActiveWorkspaceState(created);
        try {
          localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, created.id);
        } catch {
          // localStorage unavailable; non-fatal
        }
        return created;
      } catch (err) {
        if (!(err instanceof Error) || err.message === '') {
          captureException(
            err instanceof Error ? err : new Error(String(err)),
            'WorkspacesProvider.createWorkspace',
            { organizationId: activeOrg.id, userId: user.id },
          );
        }
        throw err;
      }
    },
    [activeOrg?.id, user?.id],
  );

  /**
   * Re-fetch workspaces when the active org changes. On a real org switch
   * (NOT first load) clear the persisted workspace id so a workspace from the
   * previous org can't leak into the new session. The ref-based guard avoids
   * clearing on first mount, which would discard a returning user's choice.
   */
  useEffect(() => {
    if (!activeOrg?.id) {
      lastWorkspacesOrgIdRef.current = null;
      setWorkspaces([]);
      setActiveWorkspaceState(null);
      setWorkspacesError(null);
      return;
    }
    const previous = lastWorkspacesOrgIdRef.current;
    if (previous && previous !== activeOrg.id) {
      try {
        localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY);
      } catch {
        // localStorage unavailable; non-fatal
      }
    }
    lastWorkspacesOrgIdRef.current = activeOrg.id;
    void fetchWorkspaces(activeOrg.id);
  }, [activeOrg?.id, fetchWorkspaces]);

  const value = useMemo<WorkspacesContextType>(
    () => ({
      workspaces,
      activeWorkspace,
      setActiveWorkspaceId,
      workspacesError,
      workspacesLoading,
      refreshWorkspaces,
      refreshActiveWorkspace,
      createWorkspace,
    }),
    [
      workspaces,
      activeWorkspace,
      setActiveWorkspaceId,
      workspacesError,
      workspacesLoading,
      refreshWorkspaces,
      refreshActiveWorkspace,
      createWorkspace,
    ],
  );

  return (
    <WorkspacesContext.Provider value={value}>
      {children}
    </WorkspacesContext.Provider>
  );
};

/**
 * Access the workspaces context.
 *
 * @throws Error if called outside `WorkspacesProvider`.
 */
export const useWorkspaces = (): WorkspacesContextType => {
  const context = useContext(WorkspacesContext);
  if (context === undefined) {
    throw new Error('useWorkspaces must be used within a WorkspacesProvider');
  }
  return context;
};

export default WorkspacesProvider;
